import { faAsterisk } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";

interface DatePickerState {
  label: string;
  currentValue: any;
  setCurrentValue: any;
  onUpdate?: any;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  hasMinDate?: boolean;
  hasMaxDate?: boolean;
  hasTime?: boolean;
  css?: string;
}

const DatePicker = ({
  label,
  currentValue,
  setCurrentValue,
  onUpdate = null,
  disabled = false,
  required = false,
  readOnly = false,
  hasMinDate = false,
  hasMaxDate = false,
  hasTime = false,
  css = "",
}: DatePickerState) => {
  const dateInputRef = useRef(null);
  const [draftValue, setDraftValue] = useState(currentValue);

  useEffect(() => {
    if (hasTime) {
      setDraftValue(currentValue);
    }
  }, [currentValue, hasTime]);

  const commitValue = (value: string) => {
    setCurrentValue(value);
    onUpdate?.(value);
  };

  const handleChange = (e: any) => {
    const value = e.target.value;

    if (hasTime) {
      setDraftValue(value);
    } else {
      commitValue(value);
    }
  };

  const handleBlur = (e: any) => {
    const value = hasTime ? draftValue : e.target.value;
    if (!value) return;

    const parsed = new Date(value);

    if (isNaN(parsed.getTime()) || parsed.getFullYear() < 1900) {
      setCurrentValue("");
      if (hasTime) setDraftValue("");
      return;
    }

    if (hasTime && hasMinDate) {
      const min = new Date(getMinDateTime());
      if (parsed < min) {
        setDraftValue(getMinDateTime());
        commitValue(getMinDateTime());
        return;
      }
    }

    if (hasTime) {
      commitValue(value);
    }
  };

  const getMaxDate = (withTime = false) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");

    return withTime ? `${year}-${month}-${day}T${hours}:${minutes}` : `${year}-${month}-${day}`;
  };

  const getMinDate = (withTime = false) => {
    const today = new Date();

    if (hasMinDate) {
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const hours = String(today.getHours()).padStart(2, "0");
      const minutes = String(today.getMinutes()).padStart(2, "0");

      return withTime ? `${year}-${month}-${day}T${hours}:${minutes}` : `${year}-${month}-${day}`;
    }

    const oldYear = today.getFullYear() - 100;
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");

    return withTime ? `${oldYear}-${month}-${day}T${hours}:${minutes}` : `${oldYear}-${month}-${day}`;
  };

  const getMinDateTime = () => {
    if (!hasMinDate || !hasTime) return getMinDate(true);

    const today = new Date();
    const todayDateString = today.toISOString().split("T")[0];
    const currentDateOnly = (draftValue || "").split("T")[0];

    if (currentDateOnly === todayDateString) {
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const hours = String(today.getHours()).padStart(2, "0");
      const minutes = String(today.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    return getMinDate(true);
  };

  return (
    <div className={css + " form-floating form-floating-enabled"}>
      <label className={readOnly ? "app-orange-text fw-bold" : "date-label"}>
        {label} {required && <FontAwesomeIcon className="app-tertiary-color-inverse" icon={faAsterisk} style={{ fontSize: 11 }} />}
      </label>
      <input
        data-testid="date-picker"
        data-tooltip-id={required ? "tooltip" : undefined}
        data-tooltip-content={required ? "Required" : undefined}
        id="floatingInput"
        className={
          readOnly
            ? `border-0 form-control form-control-readonly date-picker ${!draftValue && "fst-italic"}`
            : "form-control form-control-enabled date-picker"
        }
        type={hasTime ? "datetime-local" : "date"}
        value={hasTime ? draftValue : currentValue}
        onChange={(e: any) => handleChange(e)}
        ref={dateInputRef}
        disabled={disabled}
        readOnly={readOnly}
        min={hasTime ? getMinDateTime() : getMinDate(false)}
        max={hasMaxDate ? getMaxDate(hasTime) : ""}
        pattern={hasTime ? undefined : "\\d{4}-\\d{2}-\\d{2}"}
        onBlur={(e: any) => handleBlur(e)}
      />
      {required && <Tooltip id="tooltip" place="top" style={{ padding: "5px 10px", zIndex: 1100 }} />}
    </div>
  );
};

export default DatePicker;
